import { useCallback, useEffect, useMemo, useState } from 'react';

import { debounce } from '@repo/ui/lib/debounce';

/**
 * Chart utilities for responsive radius values for RadialBarChart components
 */

export interface RadialChartDimensions {
  innerRadius: number;
  outerRadius: number;
  polarRadius: [number, number];
}

export interface ChartSizeConfig {
  /** Maximum height of the chart container in pixels */
  maxHeight: number;
  /** Inner radius in pixels */
  innerRadius: number;
  /** Outer radius in pixels */
  outerRadius: number;
  /** Polar grid radii [outer, inner] in pixels */
  polarRadius: [number, number];
}

/**
 * Responsive breakpoints based on Tailwind config
 * These match the cred-* breakpoints for iframe responsive design
 */
export const RESPONSIVE_BREAKPOINTS = {
  'cred-sm': 300, // Desktop 1440x1024 & MacBook Pro 14"
  'cred-md': 350, // MacBook Pro 16"
  'cred-lg': 380, // Full HD & 2K
  'cred-xl': 500, // 4K UHD
  md: 768,
} as const;

/**
 * Fixed chart size configurations for different chart types
 * Each configuration includes pre-calculated radius values for optimal visual appearance
 */
export const CHART_SIZE_CONFIGS = {
  // Small charts (article cards, carousel items)
  small: {
    'cred-sm': {
      maxHeight: 54,
      innerRadius: 30,
      outerRadius: 17,
      polarRadius: [26, 20],
    },
    'cred-md': {
      maxHeight: 63,
      innerRadius: 35,
      outerRadius: 20,
      polarRadius: [30.5, 23.5],
    },
    'cred-lg': {
      maxHeight: 69,
      innerRadius: 38,
      outerRadius: 22,
      polarRadius: [33, 26],
    },
    'cred-xl': {
      maxHeight: 90,
      innerRadius: 50,
      outerRadius: 28,
      polarRadius: [43, 34],
    },
    md: {
      maxHeight: 96,
      innerRadius: 53,
      outerRadius: 30,
      polarRadius: [45.8, 36.5],
    },
  },
  // Medium charts (brand dashboard)
  medium: {
    'cred-sm': {
      maxHeight: 54,
      innerRadius: 31,
      outerRadius: 18,
      polarRadius: [27, 22],
    },
    'cred-md': {
      maxHeight: 63,
      innerRadius: 36,
      outerRadius: 21,
      polarRadius: [31.5, 25.5],
    },
    'cred-lg': {
      maxHeight: 68,
      innerRadius: 39,
      outerRadius: 23,
      polarRadius: [34, 27.5],
    },
    'cred-xl': {
      maxHeight: 90,
      innerRadius: 52,
      outerRadius: 30,
      polarRadius: [45, 37],
    },
    md: {
      maxHeight: 90,
      innerRadius: 52,
      outerRadius: 30,
      polarRadius: [45, 37],
    },
  },
  // Large charts (main article charts)
  large: {
    'cred-sm': {
      maxHeight: 80,
      innerRadius: 35,
      outerRadius: 59,
      polarRadius: [39, 30],
    },
    'cred-md': {
      maxHeight: 91,
      innerRadius: 40,
      outerRadius: 67,
      polarRadius: [44.5, 35],
    },
    'cred-lg': {
      maxHeight: 99,
      innerRadius: 43,
      outerRadius: 73,
      polarRadius: [49, 36.5],
    },
    'cred-xl': {
      maxHeight: 130,
      innerRadius: 57,
      outerRadius: 96,
      polarRadius: [65, 50],
    },
    md: {
      maxHeight: 130,
      innerRadius: 57,
      outerRadius: 96,
      polarRadius: [65, 50],
    },
  },
  // Extra small charts (story articles)
  extraSmall: {
    'cred-sm': {
      maxHeight: 47,
      innerRadius: 26,
      outerRadius: 15,
      polarRadius: [21, 17],
    },
    'cred-md': {
      maxHeight: 47,
      innerRadius: 26,
      outerRadius: 15,
      polarRadius: [21, 17],
    },
    'cred-lg': {
      maxHeight: 47,
      innerRadius: 26,
      outerRadius: 15,
      polarRadius: [21, 17],
    },
    'cred-xl': {
      maxHeight: 47,
      innerRadius: 26,
      outerRadius: 15,
      polarRadius: [21, 17],
    },
    md: {
      maxHeight: 47,
      innerRadius: 26,
      outerRadius: 15,
      polarRadius: [21, 17],
    },
  },
} as const;

/**
 * Gets the current responsive breakpoint based on container width
 */
function getCurrentBreakpoint(
  width: number,
): keyof typeof RESPONSIVE_BREAKPOINTS {
  if (width >= RESPONSIVE_BREAKPOINTS['md']) return 'md';
  if (width >= RESPONSIVE_BREAKPOINTS['cred-xl']) return 'cred-xl';
  if (width >= RESPONSIVE_BREAKPOINTS['cred-lg']) return 'cred-lg';
  if (width >= RESPONSIVE_BREAKPOINTS['cred-md']) return 'cred-md';
  return 'cred-sm';
}

/** The element a chart's tier is measured on (see the hook's `container`).
 *  Browser-only: it reads `document`. */
const measuredElement = (
  container: HTMLElement | null | undefined,
): HTMLElement | null => (container === undefined ? document.body : container);

/**
 * Hook for getting responsive chart dimensions based on current container size
 * Uses fixed radius values for consistent visual appearance across breakpoints
 *
 * @param chartSize - Chart size configuration ('small', 'medium', 'large', 'extraSmall')
 * @param container - The element whose width picks the tier. The article
 *   analysis passes its modal surface (`useAnalysisContainer`), the same
 *   element its `@cred-*` container queries resolve against, so the radii
 *   match the box the chart is drawn in. `null` means that element has not
 *   mounted yet, and the smallest tier stands until it does. Omitted, the
 *   page body is measured.
 * @returns Fixed chart dimensions that update responsively
 *
 * @example
 * ```tsx
 * const dimensions = useResponsiveRadialChartDimensions(
 *   'large',
 *   useAnalysisContainer(),
 * );
 *
 * return (
 *   <RadialBarChart
 *     innerRadius={dimensions.innerRadius}
 *     outerRadius={dimensions.outerRadius}
 *   >
 *     <PolarGrid polarRadius={dimensions.polarRadius} />
 *   </RadialBarChart>
 * );
 * ```
 */
export function useResponsiveRadialChartDimensions(
  chartSize: keyof typeof CHART_SIZE_CONFIGS,
  container?: HTMLElement | null,
): RadialChartDimensions {
  // Memoize the default configuration
  const defaultConfig = useMemo(
    () => CHART_SIZE_CONFIGS[chartSize]['cred-sm'],
    [chartSize],
  );

  const [dimensions, setDimensions] = useState<RadialChartDimensions>(() => {
    // Default to smallest size on initial render
    return {
      innerRadius: defaultConfig.innerRadius,
      outerRadius: defaultConfig.outerRadius,
      polarRadius: [...defaultConfig.polarRadius] as [number, number],
    };
  });

  // Memoize the update function to prevent recreation on every render
  const updateDimensions = useCallback(() => {
    const target = measuredElement(container);
    if (target) {
      // The layout width, as a container query reads it. A rect is the
      // transformed box: mid slide-in it can come back a hair under the
      // real width (379.99 for a 380px sheet) and drop a tier.
      const currentBreakpoint = getCurrentBreakpoint(target.clientWidth);
      const sizeConfig = CHART_SIZE_CONFIGS[chartSize][currentBreakpoint];

      setDimensions({
        innerRadius: sizeConfig.innerRadius,
        outerRadius: sizeConfig.outerRadius,
        polarRadius: [...sizeConfig.polarRadius] as [number, number],
      });
    }
  }, [chartSize, container]);

  // Memoize the debounced function to prevent recreation
  const debouncedUpdateDimensions = useMemo(
    () => debounce(updateDimensions, 100),
    [updateDimensions],
  );

  useEffect(() => {
    // Initial calculation
    updateDimensions();

    const resizeObserver = new ResizeObserver(debouncedUpdateDimensions);

    const target = measuredElement(container);
    if (target) {
      resizeObserver.observe(target);
    }

    return () => {
      resizeObserver.disconnect();
      debouncedUpdateDimensions.cancel();
    };
  }, [container, updateDimensions, debouncedUpdateDimensions]);

  return dimensions;
}

/**
 * Gets static responsive chart dimensions for all breakpoints
 * Returns fixed radius values for each breakpoint
 *
 * @param chartSize - Chart size configuration
 * @returns Object with fixed dimensions for each breakpoint
 */
export function getResponsiveChartDimensions(
  chartSize: keyof typeof CHART_SIZE_CONFIGS,
) {
  const sizeConfig = CHART_SIZE_CONFIGS[chartSize];

  return {
    'cred-sm': {
      innerRadius: sizeConfig['cred-sm'].innerRadius,
      outerRadius: sizeConfig['cred-sm'].outerRadius,
      polarRadius: sizeConfig['cred-sm'].polarRadius,
    },
    'cred-md': {
      innerRadius: sizeConfig['cred-md'].innerRadius,
      outerRadius: sizeConfig['cred-md'].outerRadius,
      polarRadius: sizeConfig['cred-md'].polarRadius,
    },
    'cred-lg': {
      innerRadius: sizeConfig['cred-lg'].innerRadius,
      outerRadius: sizeConfig['cred-lg'].outerRadius,
      polarRadius: sizeConfig['cred-lg'].polarRadius,
    },
    'cred-xl': {
      innerRadius: sizeConfig['cred-xl'].innerRadius,
      outerRadius: sizeConfig['cred-xl'].outerRadius,
      polarRadius: sizeConfig['cred-xl'].polarRadius,
    },
    md: {
      innerRadius: sizeConfig['md'].innerRadius,
      outerRadius: sizeConfig['md'].outerRadius,
      polarRadius: sizeConfig['md'].polarRadius,
    },
  };
}
